import React from 'react';
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { HeadingXXLarge, HeadingLarge, ParagraphLarge } from 'baseui/typography';
import { ArrowRight } from 'baseui/icon';
import { useNavigate } from 'react-router-dom';

export function HomePage() {
  const [css] = useStyletron();
  const navigate = useNavigate();

  return (
    <div className={css({
      minHeight: '100vh',
      backgroundColor: '#fff'
    })}>
      {/* Navigation */}
      <nav className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem 2rem',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(5px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        zIndex: 1000
      })}>
        <div className={css({
          fontSize: '24px',
          fontWeight: '600',
          color: '#000',
          fontFamily: 'monospace'
        })}>
          http.ly
        </div>
        <Button
          onClick={() => navigate('/services')}
          size="compact"
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: '#000',
                ':hover': {
                  backgroundColor: '#333'
                }
              }
            }
          }}
        >
          Go to Dashboard
        </Button>
      </nav>

      {/* Hero Section */}
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '10rem 2rem 8rem',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #f8f9ff 0%, #fff 100%)',
        color: '#000'
      })}>
        {/* Decorative elements */}
        <div className={css({
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: `
            radial-gradient(circle at 15% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 25%),
            radial-gradient(circle at 85% 30%, rgba(59, 130, 246, 0.08) 0%, transparent 25%)
          `,
          opacity: 0.5
        })}/>
        <div className={css({
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '20rem',
          height: '20rem',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(59, 130, 246, 0.1))',
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          filter: 'blur(40px)',
          opacity: 0.5,
          animation: 'float 8s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0)' },
            '50%': { transform: 'translate(20px, -20px)' }
          }
        })}/>
        <div className={css({
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '15rem',
          height: '15rem',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1))',
          borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%',
          filter: 'blur(40px)',
          opacity: 0.5,
          animation: 'float2 10s ease-in-out infinite',
          '@keyframes float2': {
            '0%, 100%': { transform: 'translate(0, 0)' },
            '50%': { transform: 'translate(-20px, 20px)' }
          }
        })}/>
        <div className={css({
          position: 'relative',
          zIndex: 1,
          maxWidth: '1200px'
        })}>
          <div className={css({
            display: 'inline-block',
            padding: '0.5rem 1rem',
            borderRadius: '2rem',
            background: 'rgba(99, 102, 241, 0.1)',
            color: '#6366f1',
            fontSize: '0.875rem',
            fontWeight: 500,
            marginBottom: '2rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase'
          })}>
            The Modern HTTP Toolkit
          </div>
          <HeadingXXLarge 
            marginBottom="1.5rem"
            className={css({
              fontSize: '4rem',
              lineHeight: '1.2',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: '#1a1a1a',
              position: 'relative',
              ':after': {
                content: '""',
                position: 'absolute',
                left: '50%',
                bottom: '-0.5rem',
                transform: 'translateX(-50%)',
                width: '3rem',
                height: '0.25rem',
                background: 'linear-gradient(90deg, #6366f1, #3b82f6)',
                borderRadius: '4px'
              }
            })}
          >
            Transform HTTP Traffic<br/>with Zero Code
          </HeadingXXLarge>
          <ParagraphLarge
            marginBottom="3rem"
            className={css({
              maxWidth: '700px',
              margin: '0 auto 3rem',
              color: '#666',
              fontSize: '1.25rem',
              lineHeight: '1.7',
              fontWeight: 400
            })}
          >
            Your HTTP toolkit for testing, transforming, and bridging APIs. <br></br>
            Create API gateways, and adapt services — 
            all through an intuitive interface.
          </ParagraphLarge>
          <div className={css({
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center'
          })}>
            <Button
              onClick={() => navigate('/services')}
              endEnhancer={() => <ArrowRight size={24} />}
              overrides={{
                BaseButton: {
                  style: {
                    backgroundColor: '#6366f1',
                    color: '#fff',
                    padding: '1rem 2rem',
                    ':hover': {
                      backgroundColor: '#4f46e5',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
                    },
                    transition: 'all 0.2s ease'
                  }
                }
              }}
            >
              Start Building
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className={css({
        padding: '2rem 2rem',
        backgroundColor: '#fff'
      })}>
        <div className={css({
          maxWidth: '1200px',
          margin: '0 auto'
        })}>
          <HeadingLarge
            marginBottom="4rem"
            className={css({
              textAlign: 'center',
              fontSize: '2.5rem',
              fontWeight: 600,
              letterSpacing: '-0.02em'
            })}
          >
            Powerful Yet Simple
          </HeadingLarge>
          
          <div className={css({
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem',
            margin: '0 auto'
          })}>
            {[
              {
                title: 'HTTP Gateway',
                description: 'Create personal API gateways in seconds. Route, transform, and proxy requests with point-and-click simplicity.',
                icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M4 4h16M4 12h16M4 20h16M9 8l3-4 3 4 3-4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>`
              },
              {
                title: 'Request Designer',
                description: 'Build and test HTTP requests with a modern interface. Save templates and reuse them across your projects.',
                icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M4 20h16a2 2 0 002-2V8a2 2 0 00-2-2h-7.93a2 2 0 01-1.66-.89l-.82-1.22A2 2 0 007.93 3H4a2 2 0 00-2 2v13a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 10v7M8.5 13.5l3.5 3.5 3.5-3.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>`
              },
              {
                title: 'API Bridge',
                description: 'Connect incompatible APIs effortlessly. Transform requests and responses to match different specifications.',
                icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M17 17h-3a2 2 0 01-2-2V9a2 2 0 012-2h3M7 7h3a2 2 0 012 2v6a2 2 0 01-2 2H7" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 12H4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>`
              },
              {
                title: 'Local Tunnel',
                description: 'Expose local services to the internet securely. Perfect for webhooks and API development.',
                icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M12 3v18M3 12h18M5 5l14 14M19 5L5 19" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>`
              },
              {
                title: 'Middleware Chain',
                description: 'Build custom middleware chains to modify, validate, or enhance requests. No coding required.',
                icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M8 7h12M8 12h12M8 17h12M4 7h0M4 12h0M4 17h0" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>`
              },
              {
                title: 'Real-time Monitor',
                description: 'Watch your HTTP traffic in real-time. Debug and optimize your requests with detailed insights.',
                icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 12l4-4 4 4 4-4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>`
              }
            ].map((feature, index) => (
              <div
                key={index}
                className={css({
                  padding: '2rem',
                  backgroundColor: '#fff',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  ':before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: '1px solid #f0f0f0',
                    transition: 'all 0.3s ease'
                  },
                  ':after': {
                    content: '""',
                    position: 'absolute',
                    top: '1rem',
                    left: '1rem',
                    right: '-1rem',
                    bottom: '-1rem',
                    background: 'linear-gradient(to bottom right, rgba(0,0,0,0.02), rgba(0,0,0,0.05))',
                    zIndex: -1,
                    transition: 'all 0.3s ease'
                  },
                  ':hover': {
                    transform: 'translateY(-4px)',
                    ':before': {
                      borderColor: '#000'
                    },
                    ':after': {
                      transform: 'translate(4px, 4px)'
                    }
                  }
                })}
              >
                <div className={css({
                  marginBottom: '1.5rem',
                  color: '#6366f1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '48px'
                })}
                  dangerouslySetInnerHTML={{ __html: feature.icon }}
                />
                <HeadingLarge
                  marginBottom="1rem"
                  className={css({
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em'
                  })}
                >
                  {feature.title}
                </HeadingLarge>
                <ParagraphLarge
                  className={css({
                    color: 'rgba(0, 0, 0, 0.7)',
                    margin: 0,
                    fontSize: '1rem',
                    lineHeight: '1.6'
                  })}
                >
                  {feature.description}
                </ParagraphLarge>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={css({
        padding: '4rem 2rem',
        backgroundColor: '#0A0A0A',
        color: '#fff'
      })}>
        <div className={css({
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        })}>
          <div className={css({
            fontSize: '1.2rem',
            fontWeight: '500',
            fontFamily: 'monospace'
          })}>
            http.ly
          </div>
          <div className={css({
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.875rem'
          })}>
            &copy; {new Date().getFullYear()} All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
